import Phaser from 'phaser';
import { PALETTE } from '../config';
import { createPortrait, type AthleteView } from '../game/AthleteView';
import { AudioEngine } from '../game/AudioEngine';
import { getDiscipline } from '../game/data/disciplines';
import { countryFlag } from '../game/data/flags';
import { SaveManager } from '../game/SaveManager';
import { BOOSTS, getBoost, type BoostId } from '../game/Economy';
import { t, disciplineLabel } from '../game/i18n';
import type { Athlete } from '../game/types';
import { addBackButton } from '../ui/BackButton';
import { showInstructions } from '../ui/InstructionsOverlay';
import { drawGradientBackground } from '../ui/Background';
import { Button } from '../ui/Button';
import { drawPanel } from '../ui/Panel';
import { onResize, viewport } from '../ui/viewport';

interface RosterInit {
  disciplineId: string;
  /** Preserved across swipe restarts; absent on a fresh entry (then we pick a default). */
  selectedIndex?: number;
  /** One-race boost selected before GAREGGIA (paid for on race start). */
  boostId?: BoostId;
}

interface Slot {
  view: AthleteView;
  baseX: number;
}

/** Swipeable athlete carousel: centre athlete large, neighbours peek at the sides. */
export class RosterSelect extends Phaser.Scene {
  private selectedIndex = 0;
  private disciplineId = 'sprint_100m';
  private selectedBoost?: BoostId;
  private slots: Slot[] = [];
  private dragDX = 0;
  private dragging = false;
  private startX = 0;
  private switching = false;
  private groundLineY = 0;

  constructor() {
    super('RosterSelect');
  }

  init(data: RosterInit): void {
    if (data?.disciplineId) this.disciplineId = data.disciplineId;
    this.selectedBoost = data?.boostId;
    // Fresh entry (from the discipline picker): pre-select an athlete that specialises in
    // this event, so you never inherit a stale selection from a previous discipline (which
    // is how a wrong animal could be confirmed by accident). Swipes pass selectedIndex back.
    if (typeof data?.selectedIndex === 'number') {
      this.selectedIndex = data.selectedIndex;
    } else {
      // Prefer an OWNED athlete that specialises in this event, then any owned athlete, so a
      // fresh entry never lands on a locked one by default.
      const roster = SaveManager.instance.roster;
      const owned = (a: Athlete): boolean => SaveManager.instance.isUnlocked(a);
      let i = roster.findIndex((a) => a.specialty === this.disciplineId && owned(a));
      if (i < 0) i = roster.findIndex((a) => owned(a));
      this.selectedIndex = i >= 0 ? i : 0;
    }
  }

  create(): void {
    this.slots = [];
    this.dragDX = 0;
    this.dragging = false;
    this.switching = false;
    this.build();
    const w0 = this.scale.width;
    const h0 = this.scale.height;
    onResize(this, () => {
      if (Math.abs(this.scale.width - w0) > 2 || Math.abs(this.scale.height - h0) > 2) this.scene.restart({ disciplineId: this.disciplineId, selectedIndex: this.selectedIndex, boostId: this.selectedBoost });
    });
  }

  private roster(): readonly Athlete[] {
    return SaveManager.instance.roster;
  }

  private build(): void {
    const { w, h, cx, s } = viewport(this);
    drawGradientBackground(this);

    const groundH = Math.max(96, h * 0.16);
    this.groundLineY = h - groundH;
    // Stage floor with a soft glow under the hero.
    const g = this.add.graphics();
    g.fillStyle(PALETTE.track, 1).fillRect(0, this.groundLineY, w, groundH);
    g.fillStyle(PALETTE.trackLine, 1).fillRect(0, this.groundLineY, w, 4);
    g.fillStyle(PALETTE.laneAccent, 0.18).fillEllipse(cx, this.groundLineY + 6, w * 0.4, 40);

    const d = getDiscipline(this.disciplineId);
    this.add.text(cx, h * 0.07, t('sel.chooseAthlete'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(34 * s)}px`, color: '#f4f7ff', fontStyle: 'bold',
    }).setOrigin(0.5).setShadow(0, 3, '#000000aa', 6);
    this.add.text(cx, h * 0.07 + 38 * s, `${disciplineLabel(this.disciplineId)} · ${t('sel.dragOrArrows')}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(16 * s)}px`, color: '#9fb0e0',
    }).setOrigin(0.5);

    this.helpPill(w, s);

    const n = this.roster().length;
    const centerH = Phaser.Math.Clamp(h * 0.46, 220, 400);
    // Three depth tiers (centre / neighbour / far) so the carousel reads with real perspective:
    // each step out is smaller, dimmer and pushed further toward the edge.
    const tierH = [centerH, centerH * 0.6, centerH * 0.4];
    // Cap the spacing in pixels so the far pair stays visible (not flung to the edges) on
    // very wide screens, while still reading as "further away".
    const tierX = [0, Math.min(w * 0.26, 240), Math.min(w * 0.42, 380)];
    const tierA = [1, 0.5, 0.28];
    const tierD = [6, 4, 2];

    const make = (offset: -2 | -1 | 0 | 1 | 2): void => {
      const idx = (this.selectedIndex + offset + n) % n;
      const athlete = this.roster()[idx]!;
      const lvl = Math.abs(offset);
      const x = cx + Math.sign(offset) * tierX[lvl]!;
      const view = createPortrait(this, x, this.groundLineY + 2, athlete, tierH[lvl]!);
      view.setMotion(0); // whole-body hero sprite, idle (perfectly composed)
      const locked = !SaveManager.instance.isUnlocked(athlete);
      // Locked athletes show darkened with a padlock, so the storefront reads at a glance.
      view.container.setAlpha(locked ? tierA[lvl]! * 0.5 : tierA[lvl]!).setDepth(tierD[lvl]!);
      if (locked) {
        this.add.text(x, this.groundLineY - tierH[lvl]! * 0.42, '🔒', {
          fontSize: `${Math.round((lvl === 0 ? 56 : lvl === 1 ? 34 : 24) * s)}px`,
        }).setOrigin(0.5).setDepth(tierD[lvl]! + 1).setAlpha(locked ? 0.9 : 1);
      }
      this.slots.push({ view, baseX: x });
    };
    // Far neighbours first (drawn behind), then near, then the hero — for correct overlap.
    if (n > 4) { make(-2); make(2); }
    if (n > 1) { make(-1); make(1); }
    make(0);

    const current = this.roster()[this.selectedIndex]!;
    // On tall screens the name card floats above the athlete. On short landscape phones the tall
    // hero reaches into that area, so the plate instead overlays the athlete's LOWER body (below
    // the face), with a stronger backing — never covering the face.
    const compact = h < 430;
    const cardY = compact ? this.groundLineY - centerH * 0.3 : h * 0.26;
    this.card(current, s, d.primaryStat, cardY, compact);
    this.dots(cx, compact ? this.groundLineY - 10 * s : cardY + 48 * s, s);
    this.arrow(Math.max(44, w * 0.07), this.groundLineY - centerH * 0.5, -1, s);
    this.arrow(Math.min(w - 44, w * 0.93), this.groundLineY - centerH * 0.5, 1, s);

    if (SaveManager.instance.isUnlocked(current)) {
      this.bottomBar(cx, this.groundLineY + groundH / 2, w, s);
    } else {
      this.unlockBar(current, cx, this.groundLineY + groundH / 2, w, s);
    }
    addBackButton(this, s, () => this.scene.start('DisciplineSelect'));

    this.setupDrag(w);
  }

  /** Race button plus the optional one-race boost pills, laid out as one centred row. */
  private bottomBar(cx: number, y: number, w: number, s: number): void {
    const coins = SaveManager.instance.coins;
    const pillH = Math.max(46, 52 * s);
    const pillFs = Math.max(13, Math.round(15 * s));
    const gap = Math.max(8, 10 * s);
    // Measure boost pills.
    const pills = BOOSTS.map((b) => {
      const label = `${b.emoji} ${b.price}🪙`;
      const probe = this.add.text(-999, -999, label, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${pillFs}px` });
      const pw = probe.width + 24 * s;
      probe.destroy();
      return { def: b, label, pw };
    });
    const btnW = Math.min(300, w * 0.5);
    const btnH = Math.max(64, 74 * s);
    const pillsW = pills.reduce((sum, p) => sum + p.pw, 0) + gap * pills.length;
    const totalW = pillsW + btnW;
    const pillsStartX = cx - totalW / 2;
    let x = pillsStartX;

    for (const p of pills) {
      const selected = this.selectedBoost === p.def.id;
      const afford = coins >= p.def.price;
      const cxp = x + p.pw / 2;
      const col = selected ? PALETTE.good : afford ? PALETTE.ink : PALETTE.ink;
      const g = this.add.graphics().setDepth(8);
      g.fillStyle(col, selected ? 1 : afford ? 0.55 : 0.3).fillRoundedRect(x, y - pillH / 2, p.pw, pillH, 14);
      g.lineStyle(2, selected ? PALETTE.white : PALETTE.cadence, selected ? 0.6 : 0.4).strokeRoundedRect(x, y - pillH / 2, p.pw, pillH, 14);
      this.add.text(cxp, y, p.label, {
        fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${pillFs}px`, color: selected ? '#0b1020' : afford ? '#f4f7ff' : '#8893b8', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(9);
      if (afford) {
        g.setInteractive(new Phaser.Geom.Rectangle(x, y - pillH / 2, p.pw, pillH), Phaser.Geom.Rectangle.Contains)
          .on(Phaser.Input.Events.POINTER_DOWN, () => this.toggleBoost(p.def.id));
      }
      x += p.pw + gap;
    }

    new Button(this, x + btnW / 2, y, t('sel.race'), () => this.start(), {
      width: btnW, height: btnH, fontSize: Math.round(28 * s), icon: 'play',
    });

    // Short boost caption, sitting just above the two boost pills (not over the race button).
    const boost = this.selectedBoost ? getBoost(this.selectedBoost) : undefined;
    const pillsCenterX = pillsStartX + (pillsW - gap) / 2;
    this.add.text(pillsCenterX, y - pillH / 2 - 9 * s, boost ? `${boost.emoji} ${t(`boost.${boost.id}.desc`)}` : t('sel.boostOptional'), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(11, Math.round(13 * s))}px`, color: boost ? '#6ef0a0' : '#9fb0e0', fontStyle: 'bold',
    }).setOrigin(0.5, 1).setDepth(9);
  }

  private toggleBoost(id: BoostId): void {
    AudioEngine.tap(0.4);
    this.selectedBoost = this.selectedBoost === id ? undefined : id;
    this.scene.restart({ disciplineId: this.disciplineId, selectedIndex: this.selectedIndex, boostId: this.selectedBoost });
  }

  /** Bottom bar for a LOCKED athlete: a single "unlock for N coins" button (greyed if poor). */
  private unlockBar(athlete: Athlete, cx: number, y: number, w: number, s: number): void {
    const price = athlete.unlockPrice ?? 0;
    const afford = SaveManager.instance.coins >= price;
    new Button(this, cx, y, afford ? `🔓  ${t('sel.unlock')} · ${price}🪙` : `🔒  ${t('sel.needCoins', { n: price })}`, () => this.unlock(athlete), {
      width: Math.min(360, w * 0.7), height: Math.max(72, 86 * s), fontSize: Math.round(24 * s),
      color: afford ? PALETTE.good : PALETTE.trackLine,
    });
    this.add.text(cx, y - Math.max(72, 86 * s) / 2 - 10 * s, afford ? t('sel.unlockHint') : t('sel.earnHint', { n: SaveManager.instance.coins }), {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(11, Math.round(12 * s))}px`, color: '#8a96bf',
    }).setOrigin(0.5, 1).setDepth(9);
  }

  private unlock(athlete: Athlete): void {
    if (SaveManager.instance.unlockAthlete(athlete.id)) {
      AudioEngine.chime();
      this.cameras.main.flash(220, 110, 240, 160);
      this.scene.restart({ disciplineId: this.disciplineId, selectedIndex: this.selectedIndex });
    } else {
      AudioEngine.tap(0.2);
      this.cameras.main.shake(180, 0.006);
    }
  }

  /** Rounded "how to play" pill (top-right) opening the instructions overlay. */
  private helpPill(w: number, s: number): void {
    const fs = Math.max(15, Math.round(15 * s));
    const label = this.add.text(0, 0, t('sel.howToPlay'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${fs}px`, color: '#0b1020', fontStyle: 'bold' }).setOrigin(0, 0.5);
    const icon = this.add.text(0, 0, '📖', { fontSize: `${fs + 2}px` }).setOrigin(0, 0.5);
    const padX = 14 * s; const gap = 7 * s;
    const pw = padX * 2 + icon.width + gap + label.width;
    const ph = Math.max(40, 44 * s);
    const cx = w - 16 * s - pw / 2;
    const cy = 16 * s + ph / 2;
    const g = this.add.graphics().setDepth(19);
    g.fillStyle(PALETTE.ink, 0.4).fillRoundedRect(cx - pw / 2, cy - ph / 2 + 3, pw, ph, ph / 2);
    g.fillStyle(PALETTE.cadence, 1).fillRoundedRect(cx - pw / 2, cy - ph / 2, pw, ph, ph / 2);
    g.lineStyle(2, PALETTE.white, 0.3).strokeRoundedRect(cx - pw / 2, cy - ph / 2, pw, ph, ph / 2);
    icon.setPosition(cx - pw / 2 + padX, cy).setDepth(20);
    label.setPosition(cx - pw / 2 + padX + icon.width + gap, cy).setDepth(20);
    const zone = this.add.zone(cx, cy, pw, ph).setInteractive({ useHandCursor: true }).setDepth(21);
    const open = (): void => showInstructions(this, this.disciplineId);
    zone.on(Phaser.Input.Events.POINTER_DOWN, open);
    zone.on(Phaser.Input.Events.POINTER_OVER, () => g.setAlpha(0.88));
    zone.on(Phaser.Input.Events.POINTER_OUT, () => g.setAlpha(1));
  }

  /** Horizontal swipe to change selection (with live drag feedback). */
  private setupDrag(w: number): void {
    const threshold = w * 0.12;
    const maxDrag = w * 0.3;
    this.input.on(Phaser.Input.Events.POINTER_DOWN, (p: Phaser.Input.Pointer) => {
      if (this.switching) return;
      this.dragging = true;
      this.startX = p.x;
    });
    this.input.on(Phaser.Input.Events.POINTER_MOVE, (p: Phaser.Input.Pointer) => {
      if (this.dragging) this.dragDX = Phaser.Math.Clamp(p.x - this.startX, -maxDrag, maxDrag);
    });
    const release = () => {
      if (!this.dragging) return;
      this.dragging = false;
      if (this.dragDX <= -threshold) this.step(1);
      else if (this.dragDX >= threshold) this.step(-1);
      else this.dragDX = 0; // snap back
    };
    this.input.on(Phaser.Input.Events.POINTER_UP, release);
    this.input.on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, release);
  }

  private step(dir: number): void {
    if (this.switching) return;
    this.switching = true;
    AudioEngine.resume();
    AudioEngine.tap(0.3);
    const n = this.roster().length;
    this.selectedIndex = (this.selectedIndex + dir + n) % n;
    // Slide out then rebuild for a snappy modern transition.
    this.cameras.main.fadeOut(110, 11, 16, 32);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => this.scene.restart({ disciplineId: this.disciplineId, selectedIndex: this.selectedIndex, boostId: this.selectedBoost }));
  }

  private card(athlete: Athlete, s: number, primaryStat: string, y: number, compact = false): void {
    const cx = this.scale.width / 2;
    const pw = Math.min(this.scale.width * (compact ? 0.52 : 0.62), compact ? 360 : 470);
    const ph = Math.max(compact ? 54 : 60, (compact ? 58 : 66) * s);
    // Backing sits ABOVE the athlete (depth 8) so the overlaid plate stays readable on mobile.
    drawPanel(this, cx, y, pw, ph, { radius: 14, fillAlpha: compact ? 0.86 : 0.74 }).setDepth(8);
    const nameFs = compact ? Math.max(15, Math.round(18 * s)) : Math.round(26 * s);
    this.add.text(cx, y - ph * 0.18, `${countryFlag(athlete.nationality)}  ${athlete.name}`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${nameFs}px`, color: '#f4f7ff', fontStyle: 'bold' })
      .setOrigin(0.5).setShadow(0, 3, '#000000cc', 6).setDepth(9);
    const val = athlete.stats[primaryStat as keyof typeof athlete.stats] ?? 0;
    this.add.text(cx, y + ph * 0.22, `"${athlete.nickname}" · ${athlete.species.toUpperCase()} · ${primaryStat} ${val}`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.max(12, Math.round((compact ? 13 : 15) * s))}px`, color: '#cfd8f5',
    }).setOrigin(0.5).setDepth(9);
  }

  private arrow(x: number, y: number, dir: number, s: number): void {
    const r = Math.max(30, 34 * s);
    const c = this.add.circle(x, y, r, PALETTE.ink, 0.45).setStrokeStyle(2, PALETTE.cadence, 0.7).setDepth(9);
    const t = this.add.text(x, y, dir < 0 ? '‹' : '›', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(42 * s)}px`, color: '#59d2ff', fontStyle: 'bold' }).setOrigin(0.5).setDepth(9);
    [c, t].forEach((o) => o.setInteractive({ useHandCursor: true }).on(Phaser.Input.Events.POINTER_DOWN, () => this.step(dir)));
  }

  private dots(cx: number, y: number, s: number): void {
    const n = this.roster().length;
    const gap = 20 * s;
    for (let i = 0; i < n; i++) {
      this.add.circle(cx + (i - (n - 1) / 2) * gap, y, i === this.selectedIndex ? 6 * s : 4 * s, i === this.selectedIndex ? PALETTE.laneAccent : PALETTE.trackLine, 1).setDepth(9);
    }
  }

  private start(): void {
    if (this.switching) return;
    const athlete = this.roster()[this.selectedIndex];
    if (!athlete || !SaveManager.instance.isUnlocked(athlete)) return; // locked → must unlock first
    // Charge the chosen boost at the very start (so backing out costs nothing). If the player
    // can no longer afford it, race without it rather than blocking.
    let boostId = this.selectedBoost;
    if (boostId) {
      const def = getBoost(boostId);
      if (!def || !SaveManager.instance.spendCoins(def.price)) boostId = undefined;
    }
    // Safety net: the stadium backdrop is background-loaded, so on a slow connection it might not be
    // ready yet. Every race needs it — ensure it's loaded before launching, otherwise wait briefly.
    const launch = (): void => { this.scene.start(getDiscipline(this.disciplineId).sceneKey, { athleteId: athlete.id, boostId }); };
    if (this.textures.exists('stadium')) { launch(); return; }
    this.switching = true;
    this.load.image('stadium', 'assets/backgrounds/stadium.png');
    this.load.once(Phaser.Loader.Events.COMPLETE, launch);
    this.load.start();
  }

  update(_t: number, delta: number): void {
    const dt = delta / 1000;
    for (const slot of this.slots) {
      slot.view.container.x = slot.baseX + this.dragDX;
      slot.view.update(dt);
    }
    if (!this.dragging && this.dragDX !== 0 && !this.switching) {
      this.dragDX = Phaser.Math.Linear(this.dragDX, 0, 0.2); // ease back
      if (Math.abs(this.dragDX) < 0.5) this.dragDX = 0;
    }
  }
}
